import { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import { useSearchParams, useNavigate, LoaderFunctionArgs, useLoaderData } from 'react-router-dom';
import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import {
  apiGetMostWinsWrestlerListStats,
  apiGetMostWinsWrestlerWomanListStats,
} from '../components/ui_components/ApiList';
import Pagination from '../components/stats_components/Pagination';
import { LoadingState, ErrorState } from '../components/stats_components/LoadingState';
import ContainerDetailStats from '../components/details_stats/ContainerDetailStats';
import HeaderMostFightWinsComponent from '../components/most_fights_wins_components/HeaderMostFightWinsComponent';
import { performSearchHandleSearchInputComponent } from '../components/most_fights_wins_components/HandleSearchInputComponent';
import ContainerListWrestlersComponent from '../components/most_fights_wins_components/ContainerListWrestlersComponent';
import type { WrestlerWithStats } from '../types';

interface PaginationData {
  last_page: number;
  current_page: number;
  total: number;
}

interface MostWinsStatsProps {
  isWoman?: boolean;
}

const MostWinsStats: FC<MostWinsStatsProps> = ({ isWoman = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const loaderData = useLoaderData() as { data: WrestlerWithStats[]; current_page: number; last_page: number; total: number } | null;
  const isInitialMount = useRef(true);
  const hasLoaderData = useRef(loaderData != null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [wrestlers, setWrestlers] = useState<WrestlerWithStats[]>(() =>
    loaderData && 'data' in loaderData ? loaderData.data as unknown as WrestlerWithStats[] : []
  );
  const [pagination, setPagination] = useState<PaginationData | null>(() =>
    loaderData && 'last_page' in loaderData
      ? { current_page: loaderData.current_page, last_page: loaderData.last_page, total: loaderData.total }
      : null
  );
  const [isLoading, setIsLoading] = useState<boolean>(!loaderData);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const currentPage = Number(searchParams.get('page')) || 1;
  const year = searchParams.get('year') || 'globale';

  const currentYear = new Date().getFullYear();
  const years: (string | number)[] = [
    'globale',
    ...Array.from({ length: currentYear - 2004 }, (_, i) => currentYear - i),
  ];

  const navigateToWrestler = (wrestler: WrestlerWithStats): void => {
    const wrestlerId = wrestler.id;
    const wrestlerName = wrestler.name;
    navigate(`/wrestlers/i_${wrestlerId}_d_${wrestlerName}`);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (hasLoaderData.current && currentPage === 1) return;
    }

    const controller = new AbortController();

    const fetchWrestlers = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = isWoman
          ? await apiGetMostWinsWrestlerWomanListStats(currentPage, year)
          : await apiGetMostWinsWrestlerListStats(currentPage, year);
        if (controller.signal.aborted) return;
        if ('error' in response && response.error) {
          setError('Failed to load wrestlers');
        } else if ('data' in response) {
          setWrestlers(response.data as unknown as WrestlerWithStats[]);
          setPagination({
            current_page: response.current_page,
            last_page: response.last_page,
            total: response.total,
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError('Error loading wrestlers');
          console.error('Error:', err);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchWrestlers();
    return () => controller.abort();
  }, [currentPage, year, isWoman]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handlePageChange = (newPage: number): void => {
    setSearchParams({ page: String(newPage), ...(year && { year }) });
  };

  const handleYearChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setSearchParams({ page: '1', year: e.target.value });
  };

  const handleSearchInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const searchText = e.target.value;
    setSearchInput(searchText);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchText.length === 0) {
      performSearchHandleSearchInputComponent({
        searchText: searchText,
        isWoman: isWoman,
        currentPage: currentPage || 1,
        year: year,
        setWrestlers: setWrestlers,
        setPagination: setPagination,
        setError: setError,
        setIsLoading: setIsLoading,
        apiTotal: (currentPage: number, year: string, searchText: string) =>
          apiGetMostWinsWrestlerListStats(currentPage, year, searchText),
        apiWomanTotal: (currentPage: number, year: string, searchText: string) =>
          apiGetMostWinsWrestlerWomanListStats(currentPage, year, searchText),
      });
      return;
    }

    if (searchText.length < 3) {
      return;
    }

    const timeout = setTimeout(() => {
      performSearchHandleSearchInputComponent({
        searchText: searchText,
        isWoman: isWoman,
        currentPage: currentPage || 1,
        year: year,
        setWrestlers: setWrestlers,
        setPagination: setPagination,
        setError: setError,
        setIsLoading: setIsLoading,
        apiTotal: (currentPage: number, year: string, searchText: string) =>
          apiGetMostWinsWrestlerListStats(currentPage, year, searchText),
        apiWomanTotal: (currentPage: number, year: string, searchText: string) =>
          apiGetMostWinsWrestlerWomanListStats(currentPage, year, searchText),
      });
    }, 1200);

    searchTimeoutRef.current = timeout;
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <CommonWrapper>
      <ContainerDetailStats className="container-detail-stats">
        <HeaderMostFightWinsComponent
          year={year}
          handleYearChange={handleYearChange}
          isLoading={isLoading}
          searchInput={searchInput}
          handleSearchInput={handleSearchInput}
          years={years}
          title={isWoman ? 'Numero di Vittorie Lottatrici' : 'Numero di Vittorie Lottatori'}
          currentPage={currentPage}
          lastPage={pagination?.last_page || 1}
          onPageChange={handlePageChange}
        />

        <ContainerListWrestlersComponent
          listWrestlers={wrestlers}
          navigateToWrestler={navigateToWrestler}
          beforeFights={false}
        />

        {pagination && (
          <Pagination
            currentPage={currentPage}
            lastPage={pagination.last_page}
            onPageChange={handlePageChange}
          />
        )}
      </ContainerDetailStats>
    </CommonWrapper>
  );
};

export async function loaderMostWinsStats({ request }: LoaderFunctionArgs): Promise<unknown> {
  try {
    const url = new URL(request.url);
    const year = url.searchParams.get('year');
    const response = await apiGetMostWinsWrestlerListStats(
      1,
      year || 'globale'
    );
    return response;
  } catch (error) {
    throw new Error('Failed to load data');
  }
}

export async function loaderMostWinsStatsWoman({ request }: LoaderFunctionArgs): Promise<unknown> {
  try {
    const url = new URL(request.url);
    const year = url.searchParams.get('year');
    const response = await apiGetMostWinsWrestlerWomanListStats(
      1,
      year || 'globale'
    );
    return response;
  } catch (error) {
    throw new Error('Failed to load data');
  }
}

export default MostWinsStats;
